import React, { useLayoutEffect } from 'react';
import { Paper, Typography, Box, Button, Chip, Popover } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InputDate from './InputDate';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import CollapsibleSelectFilter from './CollapsibleSelectFilter';
import CollapsibleCountryFilter from './CollapsibleCountryFilter';
import CollapsibleStatusFilter from './CollapsibleStatusFilter';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { selectFilterAction, clearSelectedFilterAction, selectFilterRemoveAction } from 'store/actions/filterActions';
import useUserRoles from 'hooks/useUserRoles';

interface CollapsibleSelectFilterProps {
  name: string;
  label: string;
  value: string;
  mockOptions: string[];
  onChange?: (selectedValues: string[]) => void;
  defaultOpen?: boolean;
}

const startDate = moment().subtract(1, 'year').startOf('month').format('YYYY-MM-DD');
const endDate = moment().endOf('month').format('YYYY-MM-DD');

type FilterPanelProps = {
  metricId: number | null;
  pendingStartDate: string | undefined;
  pendingEndDate: string | undefined;
  setPendingStartDate: React.Dispatch<React.SetStateAction<string | undefined>>;
  setPendingEndDate: React.Dispatch<React.SetStateAction<string | undefined>>;
  filterValues: any; // Or use the correct type if available, e.g., FilterValuesType
  setFilterValues: React.Dispatch<React.SetStateAction<any>>; // Or correct type
  handleDateChange: () => void;
  setForceUpdate: React.Dispatch<React.SetStateAction<number>>;
  displaySelectedCountry: any;
  setDisplaySelectedCountry: React.Dispatch<React.SetStateAction<any>>;
  displaySelectedStatus: any;
  setDisplaySelectedStatus: React.Dispatch<React.SetStateAction<any>>;
  metricPage: any;
};

const FilterPanel: React.FC<FilterPanelProps> = ({
  metricId,
  pendingStartDate,
  pendingEndDate,
  setPendingStartDate,
  setPendingEndDate,
  filterValues,
  setFilterValues,
  handleDateChange,
  setForceUpdate,
  displaySelectedCountry,
  setDisplaySelectedCountry,
  displaySelectedStatus,
  setDisplaySelectedStatus,
  metricPage,
}) => {
  const [t] = useTranslation();
  const { dispatch } = useAppState();
  const userLocale = navigator.language || 'fr-FR';
  const dateFormat = userLocale !== 'en-US' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
  const [dateFlag, setDateFlag] = React.useState<boolean>(false);
  const [textErrorHelper, setTextErrorHelper] = React.useState<string | null>(null);
  const { filter } = useSelector((state) => state);
  const userRoles = useUserRoles();

  const getInitialFilters = (id) => {
    const common = {
      country: 'ALL',
      customer_id: 'ALL',
      affair_otp: 'ALL',
    };

    if (id === metricPage[3].id) {
      return { ...common, bm_id: 'ALL' };
    }

    return {
      ...common,
      order_id: 'ALL',
      deliverable_status: 'ALL',
      client_id: 'ALL',
      consultant_id: 'ALL',
      delivery_manager: 'ALL',
      order_status: 'ALL',
    };
  };

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [activePopoverKey, setActivePopoverKey] = React.useState<string | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, key: string) => {
    setAnchorEl(event.currentTarget);
    setActivePopoverKey(key);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setActivePopoverKey(null);
  };
  React.useEffect(() => {
    setFilterValues((prevValues) => {
      const freshDefaults = getInitialFilters(metricId);
      return {
        ...freshDefaults,
        ...prevValues,
      };
    });
  }, [metricId]); // Trigger this every time the metric changes

  const handleChange = (selectedValues: any[], name: string) => {
    // Dispatch to store for API calls
    const formattedValues = Array.isArray(selectedValues)
      ? selectedValues.map((s) => s.id ?? s.name ?? s)
      : selectedValues;

    dispatch(selectFilterAction({ [name]: formattedValues }));
    setForceUpdate((prev) => prev + 1);
    // Update local state
    setFilterValues((prev) => ({
      ...prev,
      [name]: selectedValues,
    }));
  };

  // ...existing code...
  const validateDates = (start: string, end: string) => {
    const t = (key: string) => key; // Use your translation function
    const startDate = moment(start, 'YYYY-MM-DD');
    const endDate = moment(end, 'YYYY-MM-DD');

    if (!startDate.isValid() || !endDate.isValid()) {
      setTextErrorHelper(t('Date_invalid'));
      setDateFlag(true);
      return;
    }

    if (endDate.isBefore(startDate, 'day')) {
      setTextErrorHelper(t('End date should be after start date'));
      setDateFlag(true);
      return;
    }
    setTextErrorHelper(null);
    setDateFlag(false);
  };

  const handleStartDateChange = (name: string, date: moment.Moment | null) => {
    const newDate = date ? date.format('YYYY-MM-DD') : '';
    setPendingStartDate(newDate);
    validateDates(newDate, pendingEndDate ?? '');
  };

  const handleEndDateChange = (name: string, date: moment.Moment | null) => {
    const newDate = date ? date.format('YYYY-MM-DD') : '';
    setPendingEndDate(newDate);
    validateDates(pendingStartDate ?? '', newDate);
  };

  const handleDeleteChip = (key: string, value: any, label: string) => {
    const targetId = value.id ?? value.name ?? value;
    if (label === 'mainLable') {
      setFilterValues((prev) => ({
        ...prev,
        [key]: 'ALL',
      }));

      dispatch(
        selectFilterAction({
          [key]: ['ALL'],
        })
      );
      setForceUpdate((prev) => prev + 1);
    } else {
      // 1. Calculate the list of items that stay (the "remaining" values)
      const remainingItems = (filterValues[key] || []).filter(
        (item: any) => (item.id ?? item.name ?? item) !== targetId
      );
      // // 2. Extract just the IDs/Names for the dispatch payload
      const remainingIds = remainingItems.map((item: any) => item.id ?? item.name ?? item);
      // 3. Update local state with the remaining objects
      setFilterValues((prev) => ({
        ...prev,
        [key]: remainingItems,
      }));
      // 4. Dispatch the action passing ONLY the remaining values
      dispatch(
        selectFilterAction({
          [key]: remainingIds,
        })
      );
    }
  };

  const formatDate = (date: string | number | Date | moment.Moment | null | undefined): string => {
    if (!date) return '';

    return moment(date).format('YYYY-MM-DD');
  };

  const clearPerticularFilters = (apiKeyname: string) => {
    setFilterValues((prev) => ({ ...prev, [apiKeyname]: 'ALL' }));
  };
  const clearSelectedFilter = () => {
    setForceUpdate((prev) => prev + 1);
    setFilterValues(null);
    dispatch(clearSelectedFilterAction());
    setPendingStartDate(startDate);
    setPendingEndDate(endDate);
    setDisplaySelectedCountry('');

    dispatch(
      selectFilterRemoveAction({
        order_status: '',
        affair_otp: '',
        customer_id: '',
        order_id: '',
        deliverable_status: '',
        client_id: '',
        consultant_id: '',
        delivery_manager: '',
        country: '',
        bm_id: '',
      })
    );
  };

  return (
    <Paper
      sx={{
        p: 1,
        height: '90%',
        minHeight: 320,
        width: { xs: '100%', sm: 360 }, // Reduced width for desktop, full width for mobile
        maxWidth: 360,
        boxSizing: 'border-box',
        overflowY: 'auto',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 2,
          display: 'flex',
          borderBottom: 1,
          mb: 2,
          alignItems: 'center',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h2" sx={{ mr: 1 }}>
          {t('Filters')}
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={clearSelectedFilter}>
          {t('reset_all')}
        </Button>
      </Box>

      {metricId !== null && metricId <= metricPage[2].id && (
        <Box sx={{ mb: 2, p: 1, border: '1px solid #ddd', borderRadius: 1, mx: 0.5 }}>
          {/* Date Inputs (Side-by-Side) */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ border: '1px solid #bdbdbd', borderRadius: 1 }}>
              <InputDate
                label=""
                name="start_date"
                id="start_date"
                placeholder={dateFormat}
                onchange={handleStartDateChange}
                value={pendingStartDate}
                required
                variant="filled"
              />
            </Box>
            <Box sx={{ border: '1px solid #bdbdbd', borderRadius: 1 }}>
              <InputDate
                label=""
                name="end_date"
                id="end_date"
                placeholder={dateFormat}
                onchange={handleEndDateChange}
                value={pendingEndDate}
                required
                min={pendingStartDate || moment().format('YYYY-MM-DD')}
                variant="filled"
              />
            </Box>
            <Button
              variant="contained"
              disabled={dateFlag}
              onClick={() => {
                handleDateChange();
              }}
            >
              Apply
            </Button>
          </Box>
          {/* Error message below the date fields */}
          {textErrorHelper && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {textErrorHelper}
            </Typography>
          )}
        </Box>
      )}

      {/* --- Selected Filter Values as Chips --- */}
      {(() => {
        // Map internal keys to display names
        const keyDisplayMap: Record<string, string> = {
          order_status: 'status',
          affair_otp: 'otp',
          customer_id: 'customer',
          order_id: 'order',
          deliverable_status: 'Deliverable Status',
          client_id: 'client',
          consultant_id: 'consultant',
          delivery_manager: 'dm',
          country: 'country',
          bm_id: 'Bm',
        };

        const hasActiveFilters = Object.entries(filterValues ?? {}).some(([keyItem, val]) => {
          if (!val || val === '' || keyItem === 'start_date' || keyItem === 'end_date') return false;
          if (Array.isArray(val)) return val.length > 0;
          const displayVal = typeof val === 'object' ? (val as any).name : val;
          return displayVal !== 'ALL';
        });

        if (!hasActiveFilters) return null;
        return (
          Object.entries(filterValues).some(([keyItem, val]) => {
            // Basic exclusions
            if (!val || val === '' || keyItem === 'start_date' || keyItem === 'end_date') return false;

            // Array Check: Must have items and NOT be just an "All" indicator
            if (Array.isArray(val)) return val.length > 0;

            // Object/String Check: Must not be "All"
            const displayVal = typeof val === 'object' ? val.name : val;
            return displayVal !== 'ALL';
          }) && (
            <Box sx={{ mb: 2, p: 1, border: '1px solid #eee', borderRadius: 1, mx: 1, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Selected Filters
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(filterValues).map(([key, val]) => {
                  // 🛑 STRICT CHECK: Skip if value is 'All', empty, or a date range
                  if (!val || val === 'ALL' || val === '' || key === 'start_date' || key === 'end_date') return null;

                  // Additional check for empty arrays (to prevent empty chip containers)
                  if (Array.isArray(val) && val.length === 0) return null;

                  const displayKey = keyDisplayMap[key] || key;

                  if (key === 'country' || key === 'deliverable_status') {
                    const isStatus = key === 'deliverable_status';
                    const displayString = isStatus ? displaySelectedStatus : displaySelectedCountry;
                    // Only show the chip if there is a value to show
                    if (!displayString || displayString === 'ALL') return null;

                    return (
                      <Chip
                        key={key}
                        label={`${displayKey}: ${displayString}`}
                        onDelete={() => {
                          // 1. Reset filter value
                          setFilterValues((prev) => ({ ...prev, [key]: 'ALL' }));
                          clearPerticularFilters(key);
                          dispatch(selectFilterRemoveAction({ [key]: 'ALL' }));
                        }}
                        sx={{ m: 0.5 }} // Optional: add some margin
                      />
                    );
                  }

                  // Logic for Array (Multi-select)
                  if (Array.isArray(val)) {
                    const visibleItems = val.slice(0, 1);
                    const extraItems = val.slice(1);

                    return (
                      <Box
                        key={key}
                        sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                        onMouseLeave={handlePopoverClose}
                      >
                        {visibleItems.map((v: any) => (
                          <Chip
                            key={`${key}-${v.id ?? v.name ?? v}`}
                            label={`${displayKey}: ${v.name ?? v}`}
                            onDelete={() => {
                              handleDeleteChip(key, v, 'mainLable');
                            }}
                          />
                        ))}

                        {extraItems.length > 0 && (
                          <Box sx={{ display: 'inline-block' }} onMouseEnter={(e) => handlePopoverOpen(e, key)}>
                            <Chip
                              label={`+${extraItems.length} more`}
                              color="primary"
                              variant="outlined"
                              sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                            />
                            <Popover
                              sx={{ pointerEvents: 'none' }}
                              open={activePopoverKey === key && Boolean(anchorEl)}
                              anchorEl={anchorEl}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                              onClose={handlePopoverClose}
                              disableRestoreFocus
                            >
                              <Box
                                sx={{
                                  p: 2,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                  pointerEvents: 'auto',
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                  bgcolor: 'background.paper',
                                  '&::-webkit-scrollbar': { width: '6px' },
                                  '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '10px' },
                                }}
                                onMouseLeave={handlePopoverClose}
                              >
                                <Typography
                                  variant="subtitle2"
                                  color="primary"
                                  sx={{
                                    mb: 1.5,
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                  }}
                                >
                                  Other {displayKey}s
                                </Typography>
                                {extraItems.map((v: any) => (
                                  <Chip
                                    key={`${key}-${v.id ?? v.name ?? v}`}
                                    label={
                                      (v.name ?? v).length > 18 ? `${(v.name ?? v).substring(0, 15)}...` : v.name ?? v
                                    }
                                    size="small"
                                    variant="outlined"
                                    onDelete={() => {
                                      handleDeleteChip(key, v, 'popoverLable');
                                    }}
                                    sx={{
                                      justifyContent: 'space-between',
                                      width: '100%', // Makes all chips the same width in the scroll list
                                      borderRadius: '6px',
                                      borderColor: '#d1d5db',
                                      color: '#374151',
                                      fontWeight: 500,
                                      '& .MuiChip-deleteIcon': {
                                        color: '#9ca3af',
                                        '&:hover': { color: '#ef4444' }, // Turns red on hover
                                      },
                                    }}
                                  />
                                ))}
                              </Box>
                            </Popover>
                          </Box>
                        )}
                      </Box>
                    );
                  }

                  // Fallback for non-array values (String or Object)
                  // Double check here to ensure we don't render a Chip if val.name is "All"
                  const labelValue = typeof val === 'object' ? val.name : val;

                  if (labelValue === 'ALL') return null;

                  return (
                    <Chip
                      key={key}
                      label={`${displayKey}: ${labelValue}`}
                      onDelete={() => setFilterValues((prev) => ({ ...prev, [key]: 'ALL' }))}
                    />
                  );
                })}
              </Box>
            </Box>
          )
        );
      })()}

      {/* --- List of Collapsible Filters --- */}

      {/* Wrap your filters in a container that dynamically orders them */}
      <Box>
        {[
          {
            id: 'country',
            pagesToHide: [],
            render: () => (
              <CollapsibleCountryFilter
                name="country"
                apiKeyname="country"
                label="Country"
                disabled={false}
                value={filterValues?.country || []}
                mockOptions={filter?.items?.countries || {}}
                onChange={(selectedValues) => handleChange(selectedValues, 'country')}
                clearPerticularFilters={(apiKeyname) => clearPerticularFilters(apiKeyname)}
                setDisplaySelectedCountry={setDisplaySelectedCountry}
              />
            ),
          },
          {
            id: 'customer_id',
            pagesToHide: [],
            render: () => (
              <CollapsibleSelectFilter
                name="customer_id"
                apiKeyname="customer_id"
                label="Customers"
                disabled={false}
                value={filterValues?.customer_id || []}
                mockOptions={filter?.items.customers?.map((c) => ({ id: c.id ?? c.name, name: c.name }))}
                onChange={(selectedValues) => handleChange(selectedValues, 'customer_id')}
                clearPerticularFilters={(apiKeyname) => clearPerticularFilters(apiKeyname)}
              />
            ),
          },
          {
            id: 'client_id',
            pagesToHide: [metricPage[3].id],
            render: () => (
              <CollapsibleSelectFilter
                name="client_id"
                apiKeyname="client_id"
                label="Client"
                disabled={false}
                value={filterValues?.client_id || []}
                mockOptions={filter?.items?.clients?.map((c) => ({
                  id: c.id ?? `${c.first_name} ${c.last_name}`,
                  name: `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim(),
                }))}
                onChange={(selectedValues) => handleChange(selectedValues, 'client_id')}
                clearPerticularFilters={(apiKeyname) => clearPerticularFilters(apiKeyname)}
              />
            ),
          },
          {
            id: 'affair_otp',
            pagesToHide: [],
            render: () => (
              <CollapsibleSelectFilter
                name="affair_otp"
                apiKeyname="affair_otp"
                label="OTP"
                disabled={false}
                value={filterValues?.affair_otp || []}
                mockOptions={filter?.items?.otps?.map((o) => ({ id: o.id ?? o, name: o.name ?? o }))}
                onChange={(selectedValues) => handleChange(selectedValues, 'affair_otp')}
                clearPerticularFilters={(apiKeyname) => clearPerticularFilters(apiKeyname)}
              />
            ),
          },
          {
            id: 'delivery_manager',
            pagesToHide: [metricPage[3].id],
            render: () => (
              <CollapsibleSelectFilter
                name="delivery_manager"
                apiKeyname="delivery_manager"
                label="DM"
                disabled={false}
                value={filterValues?.delivery_manager || []}
                mockOptions={filter?.items?.deliverymanagers?.map((c) => ({
                  id: c.id ?? `${c.first_name} ${c.last_name}`,
                  name: `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim(),
                }))}
                onChange={(selectedValues) => handleChange(selectedValues, 'delivery_manager')}
                clearPerticularFilters={(apiKeyname) => clearPerticularFilters(apiKeyname)}
              />
            ),
          },
          {
            id: 'consultant_id',
            pagesToHide: userRoles.isClient ? [metricPage[0].id, metricPage[1].id, metricPage[2].id,metricPage[3].id] : [metricPage[3].id],
            render: () => (
              <CollapsibleSelectFilter
                name="consultant_id"
                apiKeyname="consultant_id"
                label="Consultant"
                disabled={false}
                value={filterValues?.consultant_id || []}
                mockOptions={filter?.items?.consultants?.map((c) => ({
                  id: c.id ?? `${c.first_name} ${c.last_name}`,
                  name: `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim(),
                }))}
                onChange={(selectedValues) => handleChange(selectedValues, 'consultant_id')}
                clearPerticularFilters={(apiKeyname) => clearPerticularFilters(apiKeyname)}
              />
            ),
          },
          {
            id: 'deliverable_status',
            pagesToHide: [metricPage[3].id],
            render: () => (
              <CollapsibleStatusFilter
                name="deliverable_status"
                apiKeyname="deliverable_status"
                label="Deliverable status"
                disabled={false}
                value={filterValues?.deliverable_status || []}
                mockOptions={filter?.items?.deliverable_status || {}}
                onChange={(selectedValues) => handleChange(selectedValues, 'deliverable_status')}
                clearPerticularFilters={(apiKeyname) => clearPerticularFilters(apiKeyname)}
                setDisplaySelectedStatus={setDisplaySelectedStatus}
              />
            ),
          },
          {
            id: 'order_id',
            pagesToHide: [metricPage[3].id],
            render: () => (
              <CollapsibleSelectFilter
                name="order_id"
                apiKeyname="order_id"
                label="Order ID"
                disabled={false}
                value={filterValues?.order_id || []}
                mockOptions={filter?.items?.order_ids?.map((a) => ({ id: a.id ?? a, name: a.name ?? a.toString() }))}
                onChange={(selectedValues) => handleChange(selectedValues, 'order_id')}
                clearPerticularFilters={(apiKeyname) => clearPerticularFilters(apiKeyname)}
              />
            ),
          },
          {
            id: 'order_status',
            pagesToHide: [metricPage[3].id],
            render: () => (
              <CollapsibleSelectFilter
                name="order_status"
                apiKeyname="order_status"
                label="Order type"
                disabled={false}
                value={filterValues?.order_status || []}
                mockOptions={filter?.items?.order_status?.map((s) => ({ id: s.id ?? s.value, name: s.status }))}
                onChange={(selectedValues) => handleChange(selectedValues, 'order_status')}
                clearPerticularFilters={(apiKeyname) => clearPerticularFilters(apiKeyname)}
              />
            ),
          },
          {
            id: 'bm_id',
            pagesToHide: [metricPage[0].id, metricPage[1].id, metricPage[2].id],
            render: () => (
              <CollapsibleSelectFilter
                name="bm_id"
                apiKeyname="bm_id"
                label="BM"
                disabled={false}
                value={filterValues?.bm_id || []}
                mockOptions={filter?.items?.businessmanagers?.map((c) => ({
                  id: c.id ?? `${c.first_name} ${c.last_name}`,
                  name: `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim(),
                }))}
                onChange={(selectedValues) => handleChange(selectedValues, 'bm_id')}
                clearPerticularFilters={(apiKeyname) => clearPerticularFilters(apiKeyname)}
              />
            ),
          },
        ]
          .reduce((acc, item) => {
            let activeOrder = ['country', 'customer_id', 'affair_otp']; // Default order

            switch (metricId) {
              case metricPage[0].id:
              case metricPage[1].id:
              case metricPage[2].id:
                activeOrder = [
                  'country',
                  'customer_id',
                  'client_id',
                  'affair_otp',
                  'delivery_manager',
                  'consultant_id',
                  'deliverable_status',
                  'order_id',
                  'order_status',
                ];
                break;
              case metricPage[3].id:
                activeOrder = ['country', 'customer_id', 'affair_otp', 'bm_id'];
                break;
              case metricPage[4].id:
                activeOrder = ['country', 'customer_id', 'affair_otp'];
                break;

              default:
                activeOrder = ['country', 'customer_id', 'affair_otp'];
                break;
            }

            // Check if this item is allowed on the current page AND not explicitly blocked
            const isAllowedOnPage = activeOrder.includes(item.id);
            const isExplicitlyHidden = item.pagesToHide?.includes(metricId);

            if (isAllowedOnPage && !isExplicitlyHidden) {
              // Store the sort index directly on the object to make ordering accurate
              acc.push({ ...item, sortOrderIndex: activeOrder.indexOf(item.id) });
            }
            return acc;
          }, [])
          // 3. Render clean layout items
          .map((item) => {
            const component = item?.render?.();
            if (!component) return null;
            return (
              <Box key={item.id} sx={{ px: 0.5 }}>
                {component}
              </Box>
            );
          })}
      </Box>
    </Paper>
  );
};

export default FilterPanel;
